import { type AnimationItem } from 'lottie-web';
import lottie from 'lottie-web/build/player/lottie_light';
import { useRef, useEffect, useState, type CSSProperties } from 'react';

import { type AnimationWithMeta } from './types';

type LottieProps = {
  animationData?: AnimationWithMeta | null;
  path?: string;
  autoplay?: boolean;
  loop?: boolean;
  play?: boolean;
  renderer?: 'svg';
  className?: string;
};

const Lottie = ({
  animationData = null,
  path = '',
  autoplay = false,
  renderer = 'svg',
  loop = false,
  play = false,
  className = '',
  ...props
}: LottieProps) => {
  const animElementRef = useRef<Element | null>(null);
  const animRef = useRef<AnimationItem | null | undefined>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    animRef.current = lottie.loadAnimation({
      animationData,
      path,
      container: animElementRef.current as Element,
      renderer,
      loop: false,
      autoplay: false,
    });

    function onDomLoaded() {
      setReady(true);
    }
    animRef.current.addEventListener('DOMLoaded', onDomLoaded);

    return () => {
      animRef.current?.removeEventListener('DOMLoaded', onDomLoaded);
      setReady(false);
      animRef.current?.destroy();
      animRef.current = undefined;
    };
  }, [loop, renderer, animationData, path]);

  useEffect(() => {
    if (!ready || !animRef.current) return;
    animRef.current.loop = loop;
  }, [ready, loop]);

  useEffect(() => {
    if (!ready || !animRef.current) return;

    if (play) {
      animRef.current.play();
    }

    if (autoplay && !play) {
      animRef.current.play();
    } else {
      animRef.current.stop();
    }
  }, [play, autoplay, ready]);

  const containerStyles: CSSProperties = {
    width: '100%',
    height: '100%',
  };

  return (
    <div
      className={className}
      style={containerStyles}
      {...props}
      ref={animElementRef as React.RefObject<HTMLDivElement>}
    />
  );
};

export default Lottie;
